import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { 
  Calendar as CalendarIcon, Clock, Plus, X, ChevronLeft, ChevronRight, 
  Video, MapPin, User, Edit2, Trash2, Loader2, ExternalLink, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

import { API } from '../config/api';

// Helper to get auth token
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const EVENT_TYPES = [
  { id: 'session', label: 'Training Session', color: '#02b5ff', emoji: '🏃' },
  { id: 'assessment', label: 'Assessment', color: '#ff025f', emoji: '📋' },
  { id: 'meeting', label: 'Meeting', color: '#10b981', emoji: '🤝' },
  { id: 'class', label: 'Group Class', color: '#f59e0b', emoji: '👥' },
  { id: 'competition', label: 'Competition', color: '#8b5cf6', emoji: '🏆' }
];

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = Array.from({ length: 13 }, (_, i) => i + 7); // 7am to 7pm

export default function CoachCalendar({ athletes = [] }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [view, setView] = useState('week'); // week, day
  const [googleConnected, setGoogleConnected] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncToGoogle, setSyncToGoogle] = useState(false);
  
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    event_type: 'session',
    athlete_id: '',
    athlete_name: '',
    location: '',
    meet_link: ''
  });

  const checkGoogleConnection = async () => {
    try {
      const res = await axios.get(`${API}/google-calendar/connection-status`, {
        headers: getAuthHeaders()
      });
      setGoogleConnected(res.data.connected);
    } catch (err) {
      console.error('Failed to check Google connection');
    }
  };

  const connectGoogleCalendar = async () => {
    try {
      const res = await axios.get(`${API}/google-calendar/auth-url`, {
        headers: getAuthHeaders()
      });
      window.location.href = res.data.auth_url;
    } catch (err) {
      toast.error('Failed to start Google Calendar connection');
    }
  };

  const disconnectGoogleCalendar = async () => {
    try {
      await axios.post(`${API}/google-calendar/disconnect`, {}, {
        headers: getAuthHeaders()
      });
      setGoogleConnected(false);
      toast.success('Google Calendar disconnected');
      fetchEvents();
    } catch (err) {
      toast.error('Failed to disconnect');
    }
  };

  const syncAllToGoogle = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/google-calendar/sync-all-to-google`, {}, {
        headers: getAuthHeaders()
      });
      toast.success(res.data.message);
      fetchEvents();
    } catch (err) {
      toast.error('Failed to sync events');
    } finally {
      setSyncing(false);
    }
  };

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const startOfWeek = getStartOfWeek(currentDate);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 7);
      
      const res = await axios.get(`${API}/google-calendar/events`, {
        params: {
          start_date: startOfWeek.toISOString(),
          end_date: endOfWeek.toISOString(),
          include_google: true
        },
        headers: getAuthHeaders()
      });
      setEvents(res.data.events || []);
      setGoogleConnected(res.data.google_connected);
    } catch (err) {
      console.error('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const createEvent = async () => {
    if (!newEvent.title || !newEvent.start_time || !newEvent.end_time) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      // Find athlete name if athlete_id is selected
      const athlete = athletes.find(a => a.id === newEvent.athlete_id);
      
      await axios.post(`${API}/google-calendar/events`, {
        ...newEvent,
        athlete_name: athlete ? `${athlete.first_name} ${athlete.last_name}` : newEvent.athlete_name,
        start_time: new Date(newEvent.start_time).toISOString(),
        end_time: new Date(newEvent.end_time).toISOString()
      }, {
        params: { sync_to_google: syncToGoogle && googleConnected },
        headers: getAuthHeaders()
      });
      
      toast.success(syncToGoogle && googleConnected ? 'Event created & synced to Google!' : 'Event created!');
      setShowAddModal(false);
      resetNewEvent();
      fetchEvents();
    } catch (err) {
      toast.error('Failed to create event');
    }
  };

  const deleteEvent = async (eventId, event) => {
    if (!confirm('Delete this event?')) return;
    
    try {
      await axios.delete(`${API}/google-calendar/events/${eventId}`, {
        params: { 
          delete_from_google: event?.google_event_id ? true : false,
          source: event?.source || 'local'
        },
        headers: getAuthHeaders()
      });
      toast.success('Event deleted');
      setSelectedEvent(null);
      fetchEvents();
    } catch (err) {
      console.error('Delete event error:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete event');
    }
  };

  const resetNewEvent = () => {
    setNewEvent({
      title: '',
      description: '',
      start_time: '',
      end_time: '',
      event_type: 'session',
      athlete_id: '',
      athlete_name: '',
      location: '',
      meet_link: ''
    });
  };

  const getEventStyle = (event) => {
    const eventType = EVENT_TYPES.find(t => t.id === event.event_type);
    return {
      backgroundColor: eventType?.color || '#02b5ff',
      borderLeft: `3px solid ${eventType?.color || '#02b5ff'}`
    };
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const getEventsForDay = (dayOffset) => {
    const startOfWeek = getStartOfWeek(currentDate);
    const targetDate = new Date(startOfWeek);
    targetDate.setDate(targetDate.getDate() + dayOffset);
    
    return events.filter(event => {
      const eventDate = new Date(event.start_time);
      return eventDate.toDateString() === targetDate.toDateString();
    });
  };

  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const startOfWeek = getStartOfWeek(currentDate);
    const date = new Date(startOfWeek);
    date.setDate(date.getDate() + i);
    return date;
  });

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="coach-calendar">
      {/* Header */}
      <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-[#02b5ff]" />
            Schedule
          </h2>
          
          {/* Navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigateWeek(-1)}
              className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-white font-medium min-w-[180px] text-center">
              {formatDate(weekDates[0])} - {formatDate(weekDates[6])}
            </span>
            <button
              onClick={() => navigateWeek(1)}
              className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Google Calendar Connection */}
          {googleConnected ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-green-400 flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                Google Connected
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={syncAllToGoogle}
                disabled={syncing}
                className="border-zinc-700 text-xs"
              >
                {syncing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                <span className="ml-1">Sync All</span>
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={disconnectGoogleCalendar}
                className="text-zinc-500 text-xs"
              >
                Disconnect
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={connectGoogleCalendar}
              className="border-zinc-700 text-xs"
              data-testid="connect-google-btn"
            >
              <svg className="h-4 w-4 mr-1" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Connect Google Calendar
            </Button>
          )}
          
          <Button
            size="sm"
            variant="outline"
            onClick={fetchEvents}
            className="border-zinc-700"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            onClick={() => setShowAddModal(true)}
            className="bg-[#02b5ff]"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Event
          </Button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="h-8 w-8 text-[#02b5ff] mx-auto animate-spin" />
          </div>
        ) : (
          <div className="min-w-[800px]">
            {/* Day Headers */}
            <div className="grid grid-cols-7 border-b border-zinc-800">
              {weekDates.map((date, i) => {
                const isToday = date.toDateString() === new Date().toDateString();
                return (
                  <div key={i} className={`p-3 text-center border-r border-zinc-800 last:border-r-0 ${isToday ? 'bg-[#02b5ff]/10' : ''}`}>
                    <div className="text-xs text-zinc-500">{DAYS[i]}</div>
                    <div className={`text-lg font-bold ${isToday ? 'text-[#02b5ff]' : 'text-white'}`}>
                      {date.getDate()}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Events Grid */}
            <div className="grid grid-cols-7 min-h-[400px]">
              {weekDates.map((date, dayIdx) => {
                const dayEvents = getEventsForDay(dayIdx);
                const isToday = date.toDateString() === new Date().toDateString();
                
                return (
                  <div 
                    key={dayIdx} 
                    className={`border-r border-zinc-800 last:border-r-0 p-2 min-h-[400px] ${isToday ? 'bg-[#02b5ff]/5' : ''}`}
                  >
                    {dayEvents.length === 0 ? (
                      <div className="text-center text-zinc-600 text-xs py-4">No events</div>
                    ) : (
                      <div className="space-y-2">
                        {dayEvents.map(event => (
                          <button
                            key={event.id}
                            onClick={() => setSelectedEvent(event)}
                            className="w-full text-left p-2 rounded-lg bg-opacity-20 hover:bg-opacity-30 transition-all"
                            style={getEventStyle(event)}
                          >
                            <div className="text-white font-medium text-sm truncate">{event.title}</div>
                            <div className="text-white/70 text-xs flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatTime(event.start_time)} - {formatTime(event.end_time)}
                            </div>
                            {event.athlete_name && (
                              <div className="text-white/60 text-xs flex items-center gap-1 mt-1">
                                <User className="h-3 w-3" />
                                {event.athlete_name}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Event Legend */}
      <div className="p-3 border-t border-zinc-800 flex flex-wrap gap-3">
        {EVENT_TYPES.map(type => (
          <div key={type.id} className="flex items-center gap-2 text-xs text-zinc-400">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: type.color }} />
            <span>{type.emoji} {type.label}</span>
          </div>
        ))}
      </div>

      {/* Add Event Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-md w-full">
            <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="font-bold text-white">Add Event</h3>
              <button onClick={() => { setShowAddModal(false); resetNewEvent(); }} className="text-zinc-500 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              <div>
                <label className="text-xs text-zinc-500 block mb-1">Event Type</label>
                <div className="grid grid-cols-3 gap-2">
                  {EVENT_TYPES.map(type => (
                    <button
                      key={type.id}
                      onClick={() => setNewEvent({ ...newEvent, event_type: type.id })}
                      className={`p-2 rounded-lg text-center text-xs transition-all ${
                        newEvent.event_type === type.id
                          ? 'ring-2 ring-offset-2 ring-offset-zinc-900'
                          : 'bg-zinc-800 hover:bg-zinc-700'
                      }`}
                      style={{ 
                        backgroundColor: newEvent.event_type === type.id ? type.color + '30' : undefined,
                        borderColor: type.color
                      }}
                    >
                      <span className="text-lg">{type.emoji}</span>
                      <p className="text-zinc-300 mt-1">{type.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs text-zinc-500 block mb-1">Title *</label>
                <input
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                  placeholder="e.g., Training with John"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-zinc-500 block mb-1">Start Time *</label>
                  <input
                    type="datetime-local"
                    value={newEvent.start_time}
                    onChange={(e) => setNewEvent({ ...newEvent, start_time: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 block mb-1">End Time *</label>
                  <input
                    type="datetime-local"
                    value={newEvent.end_time}
                    onChange={(e) => setNewEvent({ ...newEvent, end_time: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                  />
                </div>
              </div>

              {athletes.length > 0 && (
                <div>
                  <label className="text-xs text-zinc-500 block mb-1">Athlete (Optional)</label>
                  <select
                    value={newEvent.athlete_id}
                    onChange={(e) => setNewEvent({ ...newEvent, athlete_id: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="">Select athlete</option>
                    {athletes.map(a => (
                      <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="text-xs text-zinc-500 block mb-1">Location</label>
                <input
                  type="text"
                  value={newEvent.location}
                  onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                  placeholder="e.g., Training Ground"
                />
              </div>

              <div>
                <label className="text-xs text-zinc-500 block mb-1">Google Meet Link</label>
                <input
                  type="url"
                  value={newEvent.meet_link}
                  onChange={(e) => setNewEvent({ ...newEvent, meet_link: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                  placeholder="https://meet.google.com/..."
                />
              </div>

              <div>
                <label className="text-xs text-zinc-500 block mb-1">Description</label>
                <textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white h-20 resize-none"
                  placeholder="Additional notes..."
                />
              </div>
            </div>

            {/* Sync to Google option */}
            {googleConnected && (
              <div className="px-4 py-3 border-t border-zinc-800">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={syncToGoogle}
                    onChange={(e) => setSyncToGoogle(e.target.checked)}
                    className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-[#02b5ff]"
                  />
                  <span className="text-sm text-zinc-300">Sync to Google Calendar</span>
                  <svg className="h-4 w-4 ml-auto" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </label>
              </div>
            )}

            <div className="p-4 border-t border-zinc-800 flex gap-3">
              <Button
                variant="outline"
                onClick={() => { setShowAddModal(false); resetNewEvent(); }}
                className="flex-1 border-zinc-700"
              >
                Cancel
              </Button>
              <Button
                onClick={createEvent}
                className="flex-1 bg-[#02b5ff]"
              >
                Create Event
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-md w-full">
            <div 
              className="p-4 border-b border-zinc-800 flex items-center justify-between"
              style={{ borderBottomColor: getEventStyle(selectedEvent).backgroundColor }}
            >
              <div>
                <div className="text-xs text-zinc-500 mb-1">
                  {EVENT_TYPES.find(t => t.id === selectedEvent.event_type)?.emoji} {EVENT_TYPES.find(t => t.id === selectedEvent.event_type)?.label}
                </div>
                <h3 className="font-bold text-white text-lg">{selectedEvent.title}</h3>
              </div>
              <button onClick={() => setSelectedEvent(null)} className="text-zinc-500 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-3 text-zinc-300">
                <Clock className="h-4 w-4 text-zinc-500" />
                <span>
                  {formatDate(selectedEvent.start_time)}, {formatTime(selectedEvent.start_time)} - {formatTime(selectedEvent.end_time)}
                </span>
              </div>
              
              {selectedEvent.athlete_name && (
                <div className="flex items-center gap-3 text-zinc-300">
                  <User className="h-4 w-4 text-zinc-500" />
                  <span>{selectedEvent.athlete_name}</span>
                </div>
              )}
              
              {selectedEvent.location && (
                <div className="flex items-center gap-3 text-zinc-300">
                  <MapPin className="h-4 w-4 text-zinc-500" />
                  <span>{selectedEvent.location}</span>
                </div>
              )}
              
              {selectedEvent.meet_link && (
                <a
                  href={selectedEvent.meet_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-[#02b5ff] hover:underline"
                >
                  <Video className="h-4 w-4" />
                  <span>Join Google Meet</span>
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
              
              {selectedEvent.description && (
                <div className="pt-3 border-t border-zinc-800">
                  <p className="text-sm text-zinc-400">{selectedEvent.description}</p>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-zinc-800 flex gap-3">
              <Button
                variant="outline"
                onClick={() => deleteEvent(selectedEvent.id, selectedEvent)}
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
              <Button
                onClick={() => setSelectedEvent(null)}
                className="flex-1 bg-zinc-800"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
